/*
 * Checkbox with its label (and optional description). The whole row is the hit area.
 *   <Checkbox checked={v} onCheckedChange={setV} label={…} description={…} />
 */
import { Check, Minus } from '@phosphor-icons/react'
import { Checkbox as C } from 'radix-ui'
import { useId, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  invalid,
  id: idProp,
  name,
  className,
  'aria-describedby': describedBy,
}: {
  checked: boolean | 'indeterminate'
  onCheckedChange: (v: boolean) => void
  label?: ReactNode
  description?: ReactNode
  disabled?: boolean
  invalid?: boolean
  id?: string
  name?: string
  className?: string
  'aria-describedby'?: string
}) {
  const auto = useId()
  const id = idProp ?? `cb${auto.replace(/:/g, '')}`
  return (
    <div className={cn('flex items-start gap-3', className)}>
      <C.Root
        id={id}
        name={name}
        checked={checked}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        className={cn(
          'peer mt-0.5 grid size-5 shrink-0 place-items-center rounded-md border border-hairline-strong bg-surface text-on-brand shadow-[0_1px_1px_var(--color-control)] transition-colors',
          'hover:border-line data-[state=checked]:border-brand-600 data-[state=checked]:bg-brand-600 data-[state=indeterminate]:border-brand-600 data-[state=indeterminate]:bg-brand-600',
          'disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-danger-600 touch:mt-0',
        )}
      >
        <C.Indicator>
          {checked === 'indeterminate' ? (
            <Minus className="size-3.5" weight="bold" aria-hidden="true" />
          ) : (
            <Check className="size-3.5" weight="bold" aria-hidden="true" />
          )}
        </C.Indicator>
      </C.Root>
      {label || description ? (
        <label
          htmlFor={id}
          className="flex min-w-0 cursor-pointer flex-col gap-0.5 text-14 leading-6 text-body peer-disabled:cursor-not-allowed"
        >
          {label ? <span className="text-ink">{label}</span> : null}
          {description ? <span className="text-13 text-subtle">{description}</span> : null}
        </label>
      ) : null}
    </div>
  )
}
